import React, { useEffect, useState } from 'react';
import { RegistrationFormValues, StepProps } from './index';
import { Button, Checkbox, Col, Divider, Drawer, Form, Input, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { MailOutlined } from '@ant-design/icons';
import { nameof } from 'src/shared/utils/objects';
import './index.scss';

import { Title, Text, Link } from 'src/shared/components/typography';
import { isPersonalEmailAddress } from 'src/shared/utils/strings';
import { RuleObject } from 'antd/es/form';
import { IconX } from '@tabler/icons-react';
import PrivacyPolicy from 'src/shared/components/legal/privacy-policy';
import UseAgreement from 'src/shared/components/legal/use-agreement';
import { useClosestMedia } from 'src/shared/hooks/media-queries';
import axios from 'axios';
import { useMount } from 'ahooks';

const Step2: React.FC<StepProps> = ({ onChange, onNext, loading, data, setLoading }) => {
  const { t } = useTranslation();
  const [ok, setOk] = useState<boolean>((data?.role || '').trim().length > 0 && (data?.email || '').trim().length > 0 && !!data?.termsAccepted);
  const [tcAccepted, setTcAccepted] = useState<boolean>(!!data?.termsAccepted);

  const [openModal, setOpenModal] = useState<'None' | 'PrivacyPolicy' | 'UseAgreement'>('None');
  const email = data?.email;

  const [form] = Form.useForm<RegistrationFormValues>();

  useEffect(() => {
    if (email !== form.getFieldValue(nameof<RegistrationFormValues>('email'))) {
      form.setFieldsValue({ [nameof<RegistrationFormValues>('email')]: email });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const closestMedia = useClosestMedia();
  const isSmallView = ['xs', 'sm'].includes(closestMedia as string);

  const onFinish = async (values: Partial<RegistrationFormValues>) => {
    const { email, termsAccepted } = values;

    if (await IsEmailDisposable(email!)) {
      form.setFields([
        {
          name: nameof<RegistrationFormValues>('email'),
          errors: [t('Por favor ingrese un correo electrónico válido que no sea temporal o desechable')]
        }
      ]);
      return;
    }

    onChange({ email, termsAccepted });
    onNext({ email, termsAccepted });
  };

  const [externalProviders, setExternalProviders] = useState<{ authenticationScheme: string; displayName: string }[]>([]);

  const loadExternalProviders = async () => {
    setLoading(true);
    try {
      const result = await axios.get<{ authenticationScheme: string; displayName: string }[]>('/externalProviders');
      if (result?.status === 200 && Array.isArray(result.data)) {
        setExternalProviders(result.data);
      }
    } catch (e) {
      console.error('External Providers', e);
    } finally {
      setLoading(false);
    }
  };

  useMount(() => {
    loadExternalProviders();
  });

  const visibleExternalProviders = externalProviders;

  const role = data.role;
  const isPerson = role === 'Person';

  const returnUrl = `${window.REACT_APP_WEBSITE_BASE_URI}/onboarding/registration-completed`;

  const validateEmail = async (_: RuleObject, value: string) => {
    if (!window.REACT_APP_ALLOW_PERSONAL_EMAILS_FOR_ENTERPRISE_USERS && isPersonalEmailAddress(value)) {
      return Promise.reject(t('Por favor ingrese un correo electrónico válido que no sea personal'));
    }
    return Promise.resolve();
  };

  const IsEmailDisposable = async (email: string): Promise<boolean> => {
    setLoading(true);
    try {
      const result = await axios.get<{ isDisposable: boolean }>(`/api/v1/emailValidator/isDisposable?email=${email}`);
      return result?.data?.isDisposable ?? false;
    } catch (e) {
      console.error('Registration > IsEmailDisposable', e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="step2">
      <Title style={{ fontSize: 24 }} level={4}>
        {isPerson ? t('Únete al cambio. Date de alta.') : t('Únete y accede al mejor talento para tu proyecto.')}
      </Title>
      <Form
        form={form}
        id="registration"
        name="registration"
        initialValues={data}
        onFieldsChange={(_, allFields) => {
          setOk(allFields.every((x) => x.value && (x.errors || []).length === 0));
        }}
        onFinish={onFinish}
      >
        <Row gutter={[0, 25]} style={{ marginTop: isPerson ? 29 : 36 }}>
          <Col span={24}>
            <Form.Item
              style={{ marginBottom: !isPerson ? 4 : 0 }}
              extra={
                <div style={{ lineHeight: 1.15, marginTop: 4 }}>
                  <Text style={{ fontSize: 13 }} type="secondary" color="#7F88A1">
                    {isPerson
                      ? t('Tu correo electrónico es privado y no lo compartimos con nadie.')
                      : t('Necesitamos un correo electrónico de empresa para el registro y comunicarnos contigo. No lo compartimos con nadie.')}
                  </Text>
                </div>
              }
              name={nameof<RegistrationFormValues>('email')}
              rules={[
                { required: true, message: t('Por favor ingrese un email válido') as string, type: 'email' },
                { validator: !isPerson ? validateEmail : undefined }
              ]}
            >
              <Input
                className="j2l-form-big-input"
                disabled={loading}
                size="large"
                placeholder={(isPerson ? t('Correo electrónico') : t('Correo electrónico de empresa')) as string}
                prefix={<MailOutlined />}
                type="email"
                name="username"
                id="username"
                autoComplete="off"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item style={{ marginBottom: 0 }} name={nameof<RegistrationFormValues>('termsAccepted')} valuePropName="checked">
              <Checkbox disabled={loading} onChange={(e) => setTcAccepted(e.target.checked)}>
                <Text fontSize={16}>{t('Para continuar con el registro, debes aceptar nuestras')}</Text>{' '}
                <Link
                  color="#1A1C21"
                  fontSize={16}
                  underline
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenModal('UseAgreement');
                  }}
                >
                  {t('condiciones de uso')}
                </Link>{' '}
                <Text fontSize={16}>{t('y')}</Text>{' '}
                <Link
                  color="#1A1C21"
                  fontSize={16}
                  underline
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenModal('PrivacyPolicy');
                  }}
                >
                  {t('política de privacidad')}
                </Link>
              </Checkbox>
            </Form.Item>
          </Col>

          <Col span={24} style={{ marginTop: !isPerson ? 8 : 9 }}>
            <Button
              shape="round"
              style={{
                width: '100%',
                border: 'none',
                backgroundColor: !ok || loading ? '#F1EDE9' : undefined,
                color: !ok || loading ? '#CBCED4' : undefined
              }}
              size="large"
              type="primary"
              disabled={!ok || loading}
              htmlType="submit"
            >
              {t('Continúa')}
            </Button>
          </Col>
        </Row>
        {visibleExternalProviders.length > 0 && isPerson && (
          <Divider plain style={{ marginTop: 36, marginBottom: 13, color: tcAccepted ? undefined : '#00000040' }}>
            {t('o continúa con')}
          </Divider>
        )}
        {visibleExternalProviders.length > 0 && isPerson && (
          <Row
            gutter={[16, 16]}
            justify="center"
            style={{ display: 'flex', rowGap: 16, columnGap: 16 /*, flexDirection: isSmallView ? 'column' : 'row' */ }}
          >
            {visibleExternalProviders.map(({ displayName, authenticationScheme }) => {
              const providerUrl = `${window.REACT_APP_API_BASE_URI}/external/ExternalRegister?scheme=${encodeURIComponent(
                authenticationScheme
              )}&returnUrl=${encodeURIComponent(returnUrl)}&role=${encodeURIComponent(
                data?.role as string
              )}&termsAccepted=${tcAccepted ? 'true' : 'false'}`;
              return (
                <div key={`${displayName}_${authenticationScheme}`} style={{ display: 'flex', textAlign: 'center' }}>
                  {/* Ver el comentario equivalente en components/external-providers.tsx: antd 6 quita el
                    href al ancla cuando el boton esta disabled, asi que la navegacion no puede depender
                    de el. Este bloque es una copia de ese componente y deberia consumirlo en su lugar. */}
                  <Button
                    disabled={loading || !tcAccepted}
                    href={providerUrl}
                    ghost
                    type="default"
                    shape="round"
                    style={
                      !loading && tcAccepted
                        ? { borderColor: '#D9D9D9', color: 'black', minWidth: 114 /*, width: isSmallView ? '100%' : undefined*/ }
                        : { borderColor: '#D9D9D9', color: '#00000040', minWidth: 114 /*, width: isSmallView ? '100%' : undefined */ }
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      setLoading(true);
                      window.location.assign(providerUrl);
                    }}
                  >
                    {displayName}
                  </Button>
                </div>
              );
            })}
          </Row>
        )}
      </Form>

      <Drawer
        onClose={() => setOpenModal('None')}
        open={openModal !== 'None'}
        closable={false}
        placement="right"
        styles={{
          wrapper: { width: 'min(1257px, 98vw)' },
          body: { paddingTop: `min(5vh,57px)`, paddingBottom: `min(5vh,64px)`, paddingLeft: `min(5vw,51px)`, paddingRight: `min(5vw,51px)` }
        }}
      >
        <div style={{ position: 'relative' }}>
          <span
            style={{
              position: 'absolute',
              top: isSmallView ? 5 : 10,
              right: 0,
              backgroundColor: 'white',
              borderRadius: '50%',
              zIndex: 99999,
              height: 24,
              width: 24
            }}
          >
            <IconX style={{ cursor: 'pointer' }} stroke={2} color="#1A1C21" onClick={() => setOpenModal('None')} />
          </span>
        </div>
        {openModal === 'PrivacyPolicy' && <PrivacyPolicy title1FontSize={30} title2FontSize={20} title3FontSize={18} normalTextFontSize={16} />}
        {openModal === 'UseAgreement' && <UseAgreement title1FontSize={30} title2FontSize={20} title3FontSize={18} normalTextFontSize={16} />}
      </Drawer>
    </div>
  );
};

export default Step2;
